<template>
  <div>
    <InertiaHead :title="$page.props.pageTitle" />
    <div class="tw-relative tw-min-h-screen">
      <div class="tw-pb-16">
        <div id="top-page-content">
          <ProfileIncompleteStatusBar
            v-if="
              hasAccess(ACCESS_TYPE.GROWTHMENTOR) &&
              isProfileStatus(PROFILE_STATUS.NOT_COMPLETE)
            "
          />
          <MentorAutoDeclineStatusBar
            v-if="
              hasRole(ROLE.MENTOR) &&
              isMentorStatus(MENTOR_STATUS.HIDDEN_FROM_AUTO_DECLINE)
            "
          />
          <CurrentUserModeStatusBar
            v-if="hasRole(ROLE.MENTOR) && isActorType(ACTOR_TYPE.MENTEE)"
          />
          <BaseNavbar />
        </div>

        <NotificationsContainer />
        <transition name="fade">
          <SuccessMatchingNotification
            v-if="notificationsStore.mentorMatchingNotification.isVisible"
          />
        </transition>

        <main>
          <template v-if="!$page.url.startsWith('/thank-you-for-subscribing')">
            <template v-if="isUserLoggedIn">
              <AlertTimezone />
              <AlertInvitedToTeam />
              <AlertUpgradeToPro />
            </template>
            <AlertsContainer />
          </template>
          <slot />
        </main>
      </div>
      <BaseFooter class="tw-absolute tw-bottom-0 tw-h-8 tw-w-full" />
    </div>

    <UserProfilingWizardModal />
    <RecordingFeaturePresentationModal />
  </div>
</template>

<script setup>
import { Head as InertiaHead, usePage } from '@inertiajs/vue3'
import BaseNavbar from '@/shared/components/navbar/BaseNavbar.vue'
import BaseFooter from '@/shared/components/BaseFooter.vue'
import AlertsContainer from '@/shared/components/AlertsContainer.vue'
import NotificationsContainer from '@/shared/components/NotificationsContainer.vue'
import AlertTimezone from '@/shared/components/alerts/AlertTimezone.vue'
import AlertUpgradeToPro from '@/shared/components/alerts/AlertUpgradeToPro.vue'
import CurrentUserModeStatusBar from '@/shared/components/CurrentUserModeStatusBar.vue'
import AlertInvitedToTeam from '@/shared/components/alerts/AlertInvitedToTeam.vue'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { onBeforeMount, onMounted } from 'vue'
import { useNotificationsStore } from '@/shared/stores/notifications.js'
import { useEnvironmentVariables } from '@/shared/composables/useEnvironmentVariables.js'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import { Settings } from 'luxon'
import { useDebugger } from '@/shared/composables/useDebugger.js'
import { useBonjoroWidgetInitializer } from '@/shared/composables/useBonjoroWidgetInitializer.js'
import { useCrispChatInitializer } from '@/shared/composables/useCrispChatInitializer.js'
import MentorAutoDeclineStatusBar from '@/shared/components/MentorAutoDeclineStatusBar.vue'
import {
  ACCESS_TYPE,
  ACTOR_TYPE,
  PROFILE_STATUS,
  ROLE,
  MENTOR_STATUS,
} from '@/shared/data/permissionSystem.js'
import ProfileIncompleteStatusBar from '@/shared/components/ProfileIncompleteStatusBar.vue'
import UserProfilingWizardModal from '@/shared/components/modals/UserProfilingWizardModal.vue'
import { useModals } from '@/shared/composables/useModals.js'
import {
  RECORDING_FEATURE_PRESENTATION_MODAL_NAME,
  USER_POST_PURCHASE_PROFILING_WIZARD_MODAL_NAME,
  USER_PROFILING_WIZARD_MODAL_NAME,
} from '@/shared/data/globalVariables.js'
import SuccessMatchingNotification from '@/modules/growthmentor/matches/components/SuccessMatchingNotification.vue'
import RecordingFeaturePresentationModal from '@/shared/components/modals/RecordingFeaturePresentationModal.vue'

const { currentUser, timezone } = useCurrentUser()
const {
  isUserLoggedIn,
  hasRole,
  isActorType,
  hasAccess,
  isProfileStatus,
  isMentorStatus,
} = usePermissionSystem()
const notificationsStore = useNotificationsStore()
const { streamApiKey, streamAppId } = useEnvironmentVariables()
const { $debug } = useDebugger()
const page = usePage()
const { openModal } = useModals()

useBonjoroWidgetInitializer()
useCrispChatInitializer()

onBeforeMount(() => {
  if (
    isUserLoggedIn.value &&
    import.meta.env.VITE_DISABLE_NOTIFICATIONS_GETSTREAM_CONNECTION !== 'true'
  ) {
    notificationsStore.initGetStreamClient({
      streamApiKey: streamApiKey.value,
      streamAppId: streamAppId.value,
      userGetStreamToken: currentUser.value.getstreamToken,
      userId: currentUser.value.id,
      subscribeSuccessCallback: () => {
        $debug(
          `[GetStream: ${streamAppId.value}] Notifications channel connected`,
        )
      },
      subscribeErrorCallback: (error) => {
        $debug(
          `[GetStream: ${streamAppId.value}] Error while connecting to notifications channel: ${error}`,
        )
      },
    })
  }

  // Timezone is always set to local configuration when the Layout component is created,
  // so to synchronise any future changes, like a timezone change in profile settings or timezone synchronisation via an alert,
  // we have to reload the entire page using `window.location.reload()`.
  // You can find all the places where `window.location.reload()` is used in that case
  // by searching for the comment "TIMEZONE SYNCHRONISATION:".
  Settings.defaultZone = timezone.value
})

onMounted(() => {
  // Disable wheel scroll on input fields (type number)
  document.addEventListener('wheel', function () {
    if (document.activeElement.type === 'number') {
      document.activeElement.blur()
    }
  })

  if (page.props.profilingWizard) {
    page.props.profilingWizard.kind === 'post_purchase'
      ? openModal(USER_POST_PURCHASE_PROFILING_WIZARD_MODAL_NAME)
      : openModal(USER_PROFILING_WIZARD_MODAL_NAME)
  }
  if (page.props.recordableCallsModal) {
    openModal(RECORDING_FEATURE_PRESENTATION_MODAL_NAME)
  }
})
</script>
